import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface PWAContextType {
  deferredPrompt: any;
  showInstallPrompt: () => void;
  isInstallable: boolean;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

export const usePWA = (): PWAContextType => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

export const PWAProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    // Check if the event was already captured globally in index.html
    if ((window as any).deferredPrompt) {
      setDeferredPrompt((window as any).deferredPrompt);
      setIsInstallable(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
      console.log('beforeinstallprompt event captured in Context');
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check for appinstalled event to hide button
    const installedHandler = () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
      (window as any).deferredPrompt = null;
      console.log('App installed successfully');
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const showInstallPrompt = async () => {
    if (!deferredPrompt) {
      alert("Tombol install belum siap atau sudah digunakan. Anda juga bisa menginstall melalui ikon install di address bar browser.");
      return;
    }

    try {
      // Browsers require prompt() to be called synchronously on user gesture.
      // Calling it can fail if already used.
      await deferredPrompt.prompt();

      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);

      if (outcome === 'accepted') {
        setIsInstallable(false);
      }
    } catch (error) {
      console.error("Error during PWA installation prompt:", error);
      alert("Proses install tidak dapat dimulai. Silakan gunakan opsi 'Install' atau 'Tambahkan ke Layar Utama' dari menu browser Anda.");
    } finally {
      // We must discard the prompt after it's used once
      setDeferredPrompt(null);
      (window as any).deferredPrompt = null;
    }
  };

  return (
    <PWAContext.Provider value={{ deferredPrompt, showInstallPrompt, isInstallable }}>
      {children}
    </PWAContext.Provider>
  );
};
